/**
 * Tools that read the scene and change nothing: one object's pose, size and
 * connectors, the hierarchy as an outline, and the objects that match a query.
 */

import { Quaternion } from '@babylonjs/core/Maths/math.vector';

import type { SceneObject } from '../../core/SceneObject';
import { Pages } from '../../behaviors/pages';
import { Renderer } from '../../behaviors/renderer';
import { objectAnchors } from '../authoring/anchors';
import { outlineScene, queryObjects } from '../authoring/readOps';
import { holdingDeck } from '../pages/holdingDeck';
import { getSavedPrimitiveType } from '../../export/BuiltInPrimitives';
import { describeEditorFocus } from './focus';
import { defineTool, drawnBounds, fail, resolveRef, round } from './input';
import type { SceneTool, SceneToolHost, ToolOutcome } from './types';

/** An object's connector anchors as a tool shows them: rounded world points and directions. */
export function describeAnchors(object: SceneObject, limit = 64): Record<string, unknown> | undefined {
    const anchors = objectAnchors(object);
    if (!anchors.length) return undefined;
    const rounded = (v: { asArray(): number[] }): number[] => v.asArray().map(n => round(n, 4));
    return {
        method: 'World-space connector points; direction points out of the connector. To join two, place the part so the points meet and the directions oppose (set_transform mate does this for existing objects).',
        list: anchors.slice(0, limit).map(a => ({ name: a.name, world: rounded(a.position), direction: rounded(a.direction) })),
        ...(anchors.length > limit ? { omitted: anchors.length - limit } : {}),
    };
}

/** World bounds over the object and its descendants, or a note when nothing under it draws. */
function boundsOf(target: SceneObject): Record<string, unknown> {
    const node = target.transform;
    const drawn = drawnBounds(target);
    if (!drawn) {
        return { note: 'No drawn geometry under this node — bounds are undefined (a bare transform/group, or everything under it is at scale 0 in what shows now).' };
    }
    const { min, max } = drawn;
    const r = (v: number[]): number[] => v.map(n => round(n, 5));
    const worldSize = [max.x - min.x, max.y - min.y, max.z - min.z];
    const bounds: Record<string, unknown> = {
        method: 'Conservative envelope of transformed local mesh boxes, including descendants; rotated curves can be overestimated.',
        worldMin: r([min.x, min.y, min.z]),
        worldMax: r([max.x, max.y, max.z]),
        worldSize: r(worldSize),
        worldCenter: r([(min.x + max.x) / 2, (min.y + max.y) / 2, (min.z + max.z) / 2]),
    };
    // A world box divided by world scale is the local size only while the object is not turned in the world.
    const q = node.absoluteRotationQuaternion;
    if (Math.abs(q.x) > 1e-4 || Math.abs(q.y) > 1e-4 || Math.abs(q.z) > 1e-4) {
        bounds.localSizeNote = 'omitted: node has world rotation, so worldSize is rotation-inflated and does not map to a local BoxCollider size by dividing scale. Size the collider from the mesh\'s own local bounds instead.';
    } else {
        const s = node.absoluteScaling;
        bounds.localSize = r([worldSize[0] / (Math.abs(s.x) || 1), worldSize[1] / (Math.abs(s.y) || 1), worldSize[2] / (Math.abs(s.z) || 1)]);
    }
    return bounds;
}

/** The deck `object` is or sits in, else the scene's only deck; else what is wrong. */
function deckFor(host: SceneToolHost, object: SceneObject): SceneObject | string {
    for (let node: SceneObject | null = object; node; node = node.getParent()) {
        if (node.getBehavior(Pages)) return node;
    }
    const decks = host.scene.getSceneObjects().filter(o => o.getBehavior(Pages));
    if (decks.length === 1) return decks[0];
    if (!decks.length) return 'Error: page needs a deck (an object with Pages); the scene has none.';
    return `Error: page: the scene has ${decks.length} decks (${decks.map(d => `"${d.name}"`).join(', ')}); pass an object in the one to read.`;
}

const getTransformTool = defineTool({
    name: 'get_transform',
    verb: 'Measuring',
    edits: false,
    inputSchema: {
        type: 'object',
        properties: { object: { type: 'string' }, page: { type: 'number' } },
        required: ['object'],
    },
    async run(input, host) {
        const target = resolveRef(host, input.object);
        if (!target) return fail(`Error: Object "${String(input.object)}" not found.`);
        const read = (): Promise<ToolOutcome> => {
            const t = target.transform;
            const euler = (t.rotationQuaternion ?? Quaternion.FromEulerVector(t.rotation)).toEulerAngles();
            const shape = shapeOf(target.getSceneObjectData().src);
            const result = {
                name: target.name,
                path: target.getPath(),
                ...(shape ? { shape } : {}),
                position: t.position.asArray(),
                rotation: [euler.x, euler.y, euler.z].map(n => round(n * 180 / Math.PI, 3)),
                scale: t.scaling.asArray(),
                bounds: boundsOf(target),
                anchors: describeAnchors(target),
            };
            return Promise.resolve({ text: JSON.stringify(result, null, 2) });
        };
        const page = input.page;
        if (page === undefined) return read();
        if (typeof page !== 'number' || !Number.isInteger(page) || page < 1) return fail('Error: page must be a page number, 1 for the first.');
        const deck = deckFor(host, target);
        if (typeof deck === 'string') return fail(deck);
        const count = deck.getBehavior(Pages)!.getPageCount();
        if (page > count) return fail(`Error: page ${page}: "${deck.name}" has ${count} page${count === 1 ? '' : 's'}.`);
        return holdingDeck(deck, show => { show(page - 1); return read(); });
    },
});

/** The primitive an object's `src` makes, built-in or saved with the scene; else null: the kind a generic name ("Shape") hides. */
function shapeOf(src: string | undefined): string | null {
    return src ? getSavedPrimitiveType(src) : null;
}

/** Where an object draws, and its color, for a tree line: "  at=[x,y,z] size=[w,h,d] color=#hex", world metres; "" for what draws nothing. */
function geometryText(object: SceneObject): string {
    const bounds = drawnBounds(object);
    const r = (v: number[]): string => `[${v.map(n => round(n, 2)).join(',')}]`;
    const at = bounds
        ? `  at=${r([(bounds.min.x + bounds.max.x) / 2, (bounds.min.y + bounds.max.y) / 2, (bounds.min.z + bounds.max.z) / 2])} size=${r([bounds.max.x - bounds.min.x, bounds.max.y - bounds.min.y, bounds.max.z - bounds.min.z])}`
        : '';
    const authored: unknown = object.getBehavior(Renderer)?.getData().materials?.find(m => (m.targetMaterialIndex ?? 0) === 0)?.baseColorFactor;
    const bound = typeof authored === 'string' && authored.startsWith('$') ? object.resolveProperty(authored.slice(1)) : authored;
    const color = typeof bound === 'string' && /^#[0-9a-f]{6}/i.test(bound) ? `  color=${bound.slice(0, 7).toUpperCase()}` : '';
    return at + color;
}

/** The asset pack a `src` like `./assets/PackName/foo.glb` comes from. */
function srcPackRoot(src: string | undefined): string | null {
    if (!src) return null;
    const m = src.match(/\/assets\/([^/]+)\//);
    if (m) return m[1];
    const parts = src.replace(/^\.\//, '').split('/');
    return parts.length > 1 ? parts[parts.length - 2] : null;
}

/** What {@link describeSceneTree} lists: the tool's fields, by the same names. */
export interface SceneTreeOptions {
    root?: string;
    max_depth?: number;
    name_pattern?: string;
    behavior_type?: string;
    include_paths?: boolean;
    max_objects?: number;
}

/**
 * The scene as `get_scene_tree` answers it: one indented line per object with
 * its kind, where it draws, its color, behaviors and marks, led by what the
 * user has open. Exported for a host's per-turn scene outline.
 */
export function describeSceneTree(host: SceneToolHost, input: SceneTreeOptions = {}): { text: string } | { error: string } {
    const rootRef = typeof input.root === 'string' && input.root ? input.root : undefined;
    const root = rootRef ? resolveRef(host, rootRef) : undefined;
    if (rootRef && !root) return { error: `Error: root object "${rootRef}" not found.` };
    const maxDepth = typeof input.max_depth === 'number' ? input.max_depth : undefined;
    const maxObjects = typeof input.max_objects === 'number' && input.max_objects > 0 ? input.max_objects : undefined;
    const outline = outlineScene(host.scene, { root: root ?? undefined, maxDepth, maxObjects });

    const packCounts = new Map<string, number>();
    for (const object of host.scene.getSceneObjects()) {
        const pack = srcPackRoot(object.getSceneObjectData().src);
        if (pack) packCounts.set(pack, (packCounts.get(pack) ?? 0) + 1);
    }

    // The filters pick the lines; the indentation still shows where each sits.
    const namePattern = typeof input.name_pattern === 'string' && input.name_pattern ? input.name_pattern : undefined;
    const behaviorType = typeof input.behavior_type === 'string' && input.behavior_type ? input.behavior_type : undefined;
    let entries = outline.entries;
    if (namePattern || behaviorType) {
        const found = queryObjects(host.scene, { name: namePattern, behaviorType });
        // Only a color can be malformed, and this query names none.
        const matched = new Set('matches' in found ? found.matches.map(match => match.object) : []);
        entries = entries.filter(entry => matched.has(entry.object));
    }

    const selected = host.selected?.() ?? null;
    const lines = entries.map(({ object, path, depth, omittedChildren }) => {
        const data = object.getSceneObjectData();
        const behaviors = data.behaviors ?? [];
        const types = behaviors.map(b => b['@type']);
        const text = (behaviors.find(b => b['@type'] === 'Text') as { text?: unknown } | undefined)?.text;
        const pack = srcPackRoot(data.src);
        const shape = shapeOf(data.src);
        return '  '.repeat(depth) + object.name
            + (shape ? `  shape=${shape}` : '')
            + geometryText(object)
            + (types.length ? `  [${types.join(',')}]` : '')
            + (typeof text === 'string' && text ? `  text=${JSON.stringify(text.slice(0, 40))}` : '')
            + (pack ? `  ‹${pack}›` : '')
            + (data.active === false ? '  (inactive)' : '')
            + (object === selected ? '  (selected)' : '')
            + (omittedChildren > 0 ? `  (+${omittedChildren} children not listed)` : '')
            + (input.include_paths === true ? `  {${path}}` : '');
    });
    if (outline.entries.length < outline.total && !(namePattern || behaviorType) && !rootRef && maxDepth == null) {
        lines.push(`...${outline.total - outline.entries.length} more objects not listed; scope with root or max_depth.`);
    }

    const header = [
        `Scene: ${outline.total} objects` + (rootRef ? ` (subtree "${rootRef}")` : '') + (maxDepth != null ? `, maxDepth ${maxDepth}` : ''),
    ];
    if (packCounts.size > 0) {
        header.push(`Asset packs: ${[...packCounts.entries()].sort((a, b) => b[1] - a[1]).map(([p, n]) => `${p} (${n})`).join(', ')}`);
    }
    if (namePattern || behaviorType) header.push(`Showing ${lines.length} matching node(s).`);
    const focus = describeEditorFocus(host);
    if (focus) header.push(focus);
    return { text: `${header.join('\n')}\n\n${lines.join('\n') || '(no matching nodes)'}` };
}

const getSceneTreeTool = defineTool({
    name: 'get_scene_tree',
    verb: 'Reading the scene',
    edits: false,
    inputSchema: {
        type: 'object',
        properties: {
            root: { type: 'string' },
            max_depth: { type: 'number' },
            name_pattern: { type: 'string' },
            behavior_type: { type: 'string' },
            include_paths: { type: 'boolean' },
            max_objects: { type: 'number' },
        },
    },
    run(input, host) {
        const described = describeSceneTree(host, input);
        return Promise.resolve('error' in described ? fail(described.error) : described);
    },
});

const QUERY_FIELDS = ['name_pattern', 'behavior_type', 'color_hex', 'active', 'parent_name', 'has_src', 'limit'] as const;

const queryTool = defineTool({
    name: 'query_objects',
    verb: 'Searching the scene',
    edits: false,
    inputSchema: {
        type: 'object',
        properties: {
            name_pattern: { type: 'string' },
            behavior_type: { type: 'string' },
            color_hex: { type: 'string' },
            active: { type: 'boolean' },
            parent_name: { type: 'string' },
            has_src: { type: 'boolean' },
            limit: { type: 'number' },
        },
    },
    run(input, host) {
        // A misnamed filter ("parent") would otherwise filter nothing and list the scene.
        const unknown = Object.keys(input).filter(key => !(QUERY_FIELDS as readonly string[]).includes(key));
        if (unknown.length) {
            return Promise.resolve(fail(`Error: query_objects has no ${unknown.map(key => `"${key}"`).join(', ')}; its fields are ${QUERY_FIELDS.join(', ')}.`));
        }
        // An empty string filters nothing, as an absent field does.
        const text = (value: unknown): string | undefined => typeof value === 'string' && value ? value : undefined;
        const flag = (value: unknown): boolean | undefined => typeof value === 'boolean' ? value : undefined;
        const colorHex = text(input.color_hex);
        const result = queryObjects(host.scene, {
            name: text(input.name_pattern),
            behaviorType: text(input.behavior_type),
            color: colorHex,
            active: flag(input.active),
            parentName: text(input.parent_name),
            hasSrc: flag(input.has_src),
        }, { limit: typeof input.limit === 'number' ? input.limit : 50 });
        if ('error' in result) return Promise.resolve(fail(`Error: Invalid color_hex "${colorHex}". Use #RRGGBB or #RRGGBBAA.`));
        if (result.matches.length === 0) {
            return Promise.resolve({ text: `No objects matched (scanned ${result.total}). Adjust filters or check names.` });
        }
        // Defaults are left out, not restated: the header says so, so an absent key never reads as unknown.
        const rows = result.matches.map(({ object, parentPath, behaviors, colors }) => {
            const pos = object.transform.position;
            const row: Record<string, unknown> = { name: object.name, position: [round(pos.x, 3), round(pos.y, 3), round(pos.z, 3)] };
            const shape = shapeOf(object.getSceneObjectData().src);
            if (shape) row.shape = shape;
            if (!object.active) row.active = false;
            if (behaviors.length > 0) row.behaviors = behaviors;
            if (parentPath !== undefined) row.parent = object.getParent()?.name;
            if (colors.length > 0) {
                // `color` is the first slot set; `colors` lists every one, as the visible face is often not slot 0.
                row.color = colors[0].color;
                if (colors.length > 1) row.colors = colors.map(c => ({ index: c.slot, material: c.material, color: c.color }));
                else if (colors[0].material) row.material = colors[0].material;
            }
            return row;
        });
        const body = rows.map(row => `  ${JSON.stringify(row)}`).join(',\n');
        return Promise.resolve({ text: `Matched ${rows.length}/${result.total} objects (active omitted unless false; behaviors omitted when none):\n[\n${body}\n]` });
    },
});

export const READ_TOOLS: readonly SceneTool[] = [getTransformTool, getSceneTreeTool, queryTool];
